import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, UserPlus, UserCheck, UserX, Clock, Undo2 } from "lucide-react";
import { toast } from "sonner";
import FriendRequestModal from "@/components/FriendRequestModal";
import PostCard from "@/components/PostCard";

/**
 * Profile — public view of a member's Commons presence at /u/:username.
 * Shows name, username, bio, chronological posts, and shared collections.
 * Enforces the discoverable flag: non-discoverable + non-friend viewers see
 * a stripped "private" screen. No follower counts, no likes, no metrics.
 */
export default function Profile() {
  const { username } = useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const r = await api.get(`/commons/profile/by-username/${encodeURIComponent(username)}`);
      setProfile(r.data);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => { load(); }, [load]);

  // Cancel own outgoing request from the profile page — mirrors the same
  // action available from /commons/requests.
  async function cancelOutgoing() {
    if (busy) return;
    setBusy(true);
    try {
      // We need the request id — fetch the sender's outgoing list, find the
      // one pointed at this user. Slightly indirect but keeps the backend
      // shape simple (no /friend-status/:username needed).
      const r = await api.get("/commons/friend-requests", { params: { direction: "outgoing" } });
      const req = (r.data.requests || []).find(x => x.to_user_id === profile.user_id);
      if (req) await api.delete(`/commons/friend-requests/${req.id}`);
      toast.success("request withdrawn");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not withdraw");
    } finally {
      setBusy(false);
    }
  }

  async function unfriend() {
    if (busy) return;
    if (!window.confirm(`unfriend ${profile.name}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/commons/friends/${profile.user_id}`);
      toast.success("friendship ended");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not unfriend");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-8 pt-8 pb-24" data-testid="profile-loading">
        <p className="text-center py-12 font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--cream-mute)]">
          opening the room…
        </p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-8 pt-8 pb-24" data-testid="profile-not-found">
        <Link to="/commons/directory" className="inline-flex items-center gap-2 font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--cream-mute)] hover:text-[var(--brass)]">
          <ArrowLeft size={12} strokeWidth={1.5} />
          back to the directory
        </Link>
        <div className="text-center py-24">
          <p className="font-serif-lit text-3xl text-[var(--cream)]">no such member</p>
          <p className="mt-3 font-garamond italic text-[var(--cream-dim)]">
            there is no one at that handle.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 pt-8 pb-24" data-testid="profile-page">
      <Link
        to="/commons/directory"
        data-testid="profile-back"
        className="inline-flex items-center gap-2 font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--cream-mute)] hover:text-[var(--brass)] transition-colors"
      >
        <ArrowLeft size={12} strokeWidth={1.5} />
        back to the directory
      </Link>

      {/* Identity block */}
      <header className="mt-8 flex flex-col sm:flex-row items-center sm:items-start gap-6 text-center sm:text-left">
        {profile.picture ? (
          <img
            src={profile.picture}
            alt=""
            className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border border-[rgba(179,137,90,0.3)]"
            data-testid="profile-avatar"
          />
        ) : (
          <div
            className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[var(--bg-panel-hi)] border border-[rgba(179,137,90,0.3)] flex items-center justify-center font-serif-lit text-4xl text-[var(--brass)]"
            data-testid="profile-avatar-fallback"
          >
            {(profile.name || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--brass)] opacity-80">
            a member
          </p>
          <h1 className="mt-2 font-serif-lit text-3xl sm:text-4xl text-[var(--cream)] leading-[1.05]" data-testid="profile-name">
            {profile.name}
          </h1>
          <p className="mt-1 font-mono-lc text-[11px] tracking-[0.14em] text-[var(--cream-mute)]" data-testid="profile-username">
            @{profile.username}
          </p>
          {profile.bio && !profile.private && (
            <p className="mt-4 font-garamond italic text-[var(--cream-dim)] text-base sm:text-lg leading-relaxed" data-testid="profile-bio">
              {profile.bio}
            </p>
          )}
        </div>
      </header>

      {/* Relationship button */}
      <div className="mt-6 flex justify-center sm:justify-start">
        <FriendshipButton
          profile={profile}
          busy={busy}
          onOpenRequestModal={() => setShowRequestModal(true)}
          onCancelOutgoing={cancelOutgoing}
          onUnfriend={unfriend}
          onReload={load}
        />
      </div>

      {/* Private notice — suppressed when a friendship is already pending in
          either direction, since the respond/withdraw CTA is more useful
          than the generic 'private' card in that case. */}
      {profile.private && profile.friendship !== "incoming" && profile.friendship !== "outgoing" && (
        <div className="mt-12 text-center py-12 placed-card" data-testid="profile-private">
          <p className="font-serif-lit text-2xl text-[var(--cream)]">this member is private</p>
          <p className="mt-3 font-garamond italic text-[var(--cream-dim)] max-w-md mx-auto">
            only friends can see what they've shared. send a request to say hello.
          </p>
        </div>
      )}

      {/* Posts */}
      {!profile.private && (
        <section className="mt-14" data-testid="profile-posts">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="font-cinzel text-[11px] tracking-[0.30em] uppercase text-[var(--brass)]">
              shared
            </h2>
            <span className="flex-1 h-px bg-[rgba(179,137,90,0.24)]" />
          </div>
          {profile.posts.length === 0 ? (
            <p className="text-center py-12 font-garamond italic text-[var(--cream-dim)]" data-testid="profile-posts-empty">
              nothing shared yet.
            </p>
          ) : (
            <ul className="space-y-4">
              {profile.posts.map(p => (
                <li key={p.id}>
                  <PostCard
                    post={p}
                    isOwner={profile.is_self}
                    currentUserId={user?.user_id}
                    showAuthor={false}
                    onDeleted={(id) => setProfile(prev => prev ? { ...prev, posts: prev.posts.filter(x => x.id !== id) } : prev)}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* Shared collections */}
      {!profile.private && profile.shared_collections?.length > 0 && (
        <section className="mt-14" data-testid="profile-collections">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="font-cinzel text-[11px] tracking-[0.30em] uppercase text-[var(--brass)]">
              collections
            </h2>
            <span className="flex-1 h-px bg-[rgba(179,137,90,0.24)]" />
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {profile.shared_collections.map(c => {
              const strip = (c.gallery_photos || []).slice(0, 4);
              const inner = (
                <>
                  {c.cover_image ? (
                    <div className="aspect-[16/10] -mx-5 -mt-5 mb-4 overflow-hidden">
                      <img src={c.cover_image} alt="" className="w-full h-full object-cover" />
                    </div>
                  ) : null}
                  <p className="font-serif-lit text-[var(--cream)] text-lg">{c.name}</p>
                  {c.description && (
                    <p className="mt-1 font-garamond italic text-[var(--cream-dim)] line-clamp-2">
                      {c.description}
                    </p>
                  )}
                  {strip.length > 0 && (
                    <div className="mt-3 flex gap-1" data-testid={`profile-collection-strip-${c.id}`}>
                      {strip.map((src, i) => (
                        <img key={i} src={src} alt="" className="w-10 h-10 object-cover rounded-[2px] border border-[rgba(179,137,90,0.2)]" />
                      ))}
                    </div>
                  )}
                </>
              );
              const linkTarget = c.share_token
                ? `/c/${c.share_token}?from=${encodeURIComponent(profile.username)}`
                : null;
              return (
                <li key={c.id}>
                  {linkTarget ? (
                    <Link
                      to={linkTarget}
                      data-testid={`profile-collection-${c.id}`}
                      className="placed-card p-5 block hover:border-[var(--brass)] overflow-hidden"
                    >
                      {inner}
                    </Link>
                  ) : (
                    <div
                      data-testid={`profile-collection-${c.id}`}
                      className="placed-card p-5 overflow-hidden"
                    >
                      {inner}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {showRequestModal && (
        <FriendRequestModal
          target={profile}
          onClose={() => setShowRequestModal(false)}
          onSent={() => { setShowRequestModal(false); load(); }}
        />
      )}
    </div>
  );
}

function FriendshipButton({ profile, busy, onOpenRequestModal, onCancelOutgoing, onUnfriend, onReload }) {
  const status = profile.friendship;

  if (status === "self") {
    return (
      <Link to="/settings" data-testid="profile-edit-btn" className="lc-btn-ghost">
        edit your profile
      </Link>
    );
  }

  if (status === "friends") {
    return (
      <button
        type="button"
        onClick={onUnfriend}
        disabled={busy}
        data-testid="profile-unfriend-btn"
        className="lc-btn-ghost inline-flex items-center gap-2"
      >
        <UserCheck size={12} strokeWidth={1.6} />
        friends — unfriend
      </button>
    );
  }

  if (status === "outgoing") {
    return (
      <button
        type="button"
        onClick={onCancelOutgoing}
        disabled={busy}
        data-testid="profile-cancel-request-btn"
        className="lc-btn-ghost inline-flex items-center gap-2"
      >
        <Undo2 size={12} strokeWidth={1.6} />
        request pending — withdraw
      </button>
    );
  }

  if (status === "incoming") {
    return (
      <Link
        to="/commons/requests"
        data-testid="profile-view-request-btn"
        className="lc-btn inline-flex items-center gap-2"
      >
        <Clock size={12} strokeWidth={1.8} />
        they sent you a request — respond
      </Link>
    );
  }

  // status === "none"
  if (profile.who_can_request === "none") {
    return (
      <button
        type="button"
        disabled
        data-testid="profile-cannot-request-btn"
        className="lc-btn-ghost inline-flex items-center gap-2 cursor-not-allowed opacity-70"
      >
        <UserX size={12} strokeWidth={1.6} />
        not accepting requests
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpenRequestModal}
      data-testid="profile-add-friend-btn"
      className="lc-btn inline-flex items-center gap-2"
    >
      <UserPlus size={12} strokeWidth={1.8} />
      add friend
    </button>
  );
}
